import React, { useEffect, useState } from "react";
import { Box, Button, Group, Loader, Textarea, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import HttpClient from "../helpers/HttpClient";
import { InvoiceSetting } from "../types/invoice-setting.type";
import { useLanguage } from "../providers/LanguageContext";

const client = new HttpClient<InvoiceSetting>("/invoice-setting");

const InvoiceSettings: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<InvoiceSetting>({
    id: 1,
    senderAddressLine: "",
    footerLeft: "",
    footerCenter: "",
    footerRight: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await client.getSingle();
        if (data) setSettings(data);
      } catch {
        notifications.show({ message: t.invoiceSettings.loadFailed, color: "red" });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await client.put(settings);
      notifications.show({ message: t.invoiceSettings.saveSuccess, color: "green" });
    } catch {
      notifications.show({ message: t.invoiceSettings.saveFailed, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader mt="xl" />;

  return (
    <Box mx="auto" maw={700} mt="lg" px="md">
      <Group justify="space-between" mb="md">
        <Title order={3}>{t.invoiceSettings.title}</Title>
        <Group>
          <Button onClick={handleSave} loading={saving}>
            {t.common.save}
          </Button>
          <Button variant="subtle" onClick={() => navigate("/invoice")}>
            {t.common.back}
          </Button>
        </Group>
      </Group>

      <Textarea
        label={t.invoiceSettings.senderAddressLineLabel}
        rows={2}
        mb="md"
        value={settings.senderAddressLine}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setSettings((s) => ({ ...s, senderAddressLine: value }));
        }}
      />
      <Textarea
        label={t.invoiceSettings.footerLeftLabel}
        rows={4}
        mb="md"
        value={settings.footerLeft ?? ""}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setSettings((s) => ({ ...s, footerLeft: value }));
        }}
      />
      <Textarea
        label={t.invoiceSettings.footerCenterLabel}
        rows={4}
        mb="md"
        value={settings.footerCenter ?? ""}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setSettings((s) => ({ ...s, footerCenter: value }));
        }}      />
      <Textarea
        label={t.invoiceSettings.footerRightLabel}
        rows={4}
        mb="md"
        value={settings.footerRight ?? ""}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setSettings((s) => ({ ...s, footerRight: value }));
        }}
      />
      <TextInput
        label="ZVR-Zahl (für ELDA L19 Export)"
        description="10-stellige ZVR-Zahl des Vereins"
        mb="md"
        value={settings.zvrNumber ?? ""}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setSettings((s) => ({ ...s, zvrNumber: value }));
        }}
      />
      <TextInput
        label="Steuernummer (für ELDA L19 Export)"
        description="9-stellige Steuernummer (optional)"
        mb="md"
        value={settings.steuerNummer ?? ""}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setSettings((s) => ({ ...s, steuerNummer: value }));
        }}
      />
    </Box>
  );
};

export default InvoiceSettings;
